import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import apiService from '../services/api';

interface Category {
  id: number;
  name: string;
  slug: string;
  type: string;
  parent_id: number | null;
}

interface BrandWithCategories {
  id: number;
  name: string;
  slug: string;
  categories: Category[];
}

interface CollectionNavigationProps {
  className?: string;
  isMobile?: boolean;
  onLinkClick?: () => void;
  isActive?: boolean;
}

const CollectionNavigation: React.FC<CollectionNavigationProps> = ({ 
  className = '', 
  isMobile = false, 
  onLinkClick,
  isActive = false
}) => {
  const [brands, setBrands] = useState<BrandWithCategories[]>([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [expandedBrands, setExpandedBrands] = useState<Set<number>>(new Set());

  useEffect(() => {
    fetchBrandsWithCategories();
  }, []);

  const fetchBrandsWithCategories = async () => {
    try {
      setLoading(true);
      const response = await apiService.get('/brands/navigation');
      
      if (response.success && response.data) {
        setBrands(response.data);
      }
    } catch (error) {
      // Error handled silently
    } finally {
      setLoading(false);
    }
  };

  const handleLinkClick = (slug: string) => {
    if (onLinkClick) onLinkClick();
    setIsDropdownOpen(false);
  };

  const toggleBrandExpansion = (brandId: number) => {
    setExpandedBrands(prev => {
      const newSet = new Set(prev);
      if (newSet.has(brandId)) {
        newSet.delete(brandId);
      } else {
        newSet.add(brandId);
      }
      return newSet;
    });
  };

  if (isMobile) {
    return (
      <div className={className}>
        <div className="space-y-1">
          {loading ? (
            <div className="px-4 py-3 text-sm text-gray-500">Loading collections...</div>
          ) : (
            <div className="space-y-1">
              {brands.map((brand) => (
                <div key={brand.id}>
                  {/* Main Brand - Only show expand button if has categories */}
                  {brand.categories && brand.categories.length > 0 ? (
                    <button
                      onClick={() => toggleBrandExpansion(brand.id)}
                      className="w-full flex items-center justify-between px-4 py-3 text-base font-medium text-gray-700 hover:bg-gray-100 hover:text-gray-900 rounded-xl transition-all duration-200"
                    >
                      <div className="flex items-center">
                        <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" />
                        </svg>
                        {brand.name}
                      </div>
                      <ChevronDownIcon 
                        className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${
                          expandedBrands.has(brand.id) ? 'rotate-180' : 'rotate-0'
                        }`} 
                      />
                    </button>
                  ) : (
                    <Link
                      to={`/product-signature?brand=${brand.slug}`}
                      className="flex items-center px-4 py-3 text-base font-medium text-gray-700 hover:bg-gray-100 hover:text-gray-900 rounded-xl transition-all duration-200"
                      onClick={() => handleLinkClick(brand.slug)}
                    >
                      <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" />
                      </svg>
                      {brand.name}
                    </Link>
                  )}
                  
                  {/* Brand Categories - Only show when expanded */}
                  {brand.categories && brand.categories.length > 0 && expandedBrands.has(brand.id) && (
                    <div className="ml-6 space-y-1 mt-1 animate-in slide-in-from-top-2 duration-200">
                      {brand.categories.map((category) => (
                        <Link
                          key={category.id}
                          to={`/product-signature?brand=${brand.slug}&category=${category.slug}`}
                          className="flex items-center px-4 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-50 rounded-lg transition-all duration-200"
                          onClick={() => handleLinkClick(category.slug)}
                        >
                          <span className="w-1.5 h-1.5 bg-gray-400 rounded-full mr-3"></span>
                          {category.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`relative group ${className}`}>
      <button 
        className={`transition-colors text-sm font-medium flex items-center ${
          isActive 
            ? 'text-gray-900 border-b-2 border-gray-900 pb-1' 
            : 'text-gray-600 hover:text-gray-900'
        }`}
        onMouseEnter={() => setIsDropdownOpen(true)}
        onMouseLeave={() => setIsDropdownOpen(false)}
      >
        Collection
        <ChevronDownIcon className={`ml-1 h-4 w-4 transition-transform duration-200 ${
          isDropdownOpen ? 'rotate-180' : 'rotate-0'
        }`} />
      </button>
      
      {/* Desktop Dropdown */}
      <div 
        className={`absolute top-full mt-2 bg-white shadow-lg border border-gray-200 rounded-lg z-50 transition-all duration-200 ${
          isDropdownOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
        }`}
        style={{ 
          width: '1000px',
          left: '-200px'
        }}
        onMouseEnter={() => setIsDropdownOpen(true)}
        onMouseLeave={() => setIsDropdownOpen(false)}
      >
        {loading ? (
          <div className="p-8 text-center text-gray-500">
            Loading brands...
          </div>
        ) : (
          <div className="max-w-7xl mx-auto px-8 py-8 max-h-[70vh] overflow-y-auto pr-2">
            <div className="grid grid-cols-4 gap-12">
              {brands.map((brand) => (
                <div key={brand.id} className="space-y-4">
                  <Link
                    to={`/product-signature?brand=${brand.slug}`}
                    className="block text-sm font-bold text-gray-900 pb-3 border-b border-gray-200 hover:text-gray-900"
                    onClick={() => handleLinkClick(brand.slug)}
                  >
                    {brand.name.toUpperCase()}
                  </Link>
                  {brand.categories && brand.categories.length > 0 && (
                    <div className="space-y-3">
                      {brand.categories.map((category) => (
                        <Link
                          key={category.id}
                          to={`/product-signature?brand=${brand.slug}&category=${category.slug}`}
                          className="block text-xs text-gray-600 hover:text-gray-900 transition-colors leading-relaxed"
                          onClick={() => handleLinkClick(category.slug)}
                        >
                          {category.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
            
            {brands.length === 0 && !loading && (
              <div className="p-8 text-center text-gray-500 text-sm">
                No brands with categories available
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CollectionNavigation;
